import { ref, unref, reactive, type MaybeRef } from 'vue'
import { useAPI } from './api'
import type { Deal, Agent } from '@repo/types'

export type InquiryResponse =
  | { code: 'succeeded'; shareUrl: string }
  | { code: 'email_verification_needed' }
  | { code: 'failed'; error: { code: 'INVALID_PHONE' | 'INVALID_EMAIL' | 'UNKNOWN' } }

export interface InquiryForm {
  purpose: string
  notes: string
  email: string
  phone: string
  firstName: string
  lastName: string
}

export function useInquiryForm(deal: MaybeRef<Deal | undefined>, agent: MaybeRef<Agent>, options: { app?: string } = {}) {
  const api = useAPI()

  const form = reactive<InquiryForm>({
    purpose: '物件を見学したい',
    notes: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
  })

  const submitting = ref(false)
  const result = ref<InquiryResponse | null>(null)
  const verificationCode = ref('')
  const verificationId = ref<Date | null>(null)
  const step = ref<'form' | 'verification' | 'success'>('form')

  const reset = () => {
    form.purpose = '物件を見学したい'
    form.notes = ''
    form.email = ''
    form.phone = ''
    form.firstName = ''
    form.lastName = ''
    result.value = null
    verificationCode.value = ''
    step.value = 'form'
  }

  const submit = async (code = ''): Promise<InquiryResponse> => {
    if (!unref(deal)) return { code: 'failed', error: { code: 'UNKNOWN' } }

    submitting.value = true
    try {
      const { data: { token } } = await api.call('visitorToken', unref(agent).id, unref(agent).deal.meta?.documentId)

      const requestId = new Date().toISOString()

      const response = await api.call('inquiry', token, {
        ...form,
        requestedAt: code || verificationCode.value ? verificationId.value : requestId,
        code: code || verificationCode.value,
        app: options.app || 'ja',
      })

      const data = response.data as InquiryResponse
      result.value = data

      if (data.code === 'email_verification_needed') {
        step.value = 'verification'
        verificationId.value = requestId
      } else if (data.code === 'succeeded') {
        step.value = 'success'
      }

      return data
    } catch (error) {
      console.error('Inquiry submission failed:', error)
      return { code: 'failed', error: { code: 'UNKNOWN' } }
    } finally {
      submitting.value = false
    }
  }

  return {
    form,
    submitting,
    result,
    verificationCode,
    step,
    submit,
    reset,
  }
}
